import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowLeft } from 'lucide-react';

/* ── Design tokens ── */
export const GOLD     = '#B8975A';
export const NAVY     = '#080e1a';
export const GOLD_DIM = 'rgba(184,151,90,0.12)';
export const GOLD_MID = 'rgba(184,151,90,0.28)';
export const CR       = '#F5F3EF';
export const W        = '#FAFAF8';

const GRAIN = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`;

/* ── Global styles ── */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Jost:wght@300;400;500;600&display=swap');

    *, *::before, *::after { box-sizing: border-box; }
    html, body { overflow-x: clip; } /* clip instead of hidden — hidden creates a scroll container that kills position:sticky */

    .f-cormorant { font-family: 'Cormorant Garamond', serif; }
    .f-jost      { font-family: 'Jost', sans-serif; }

    @keyframes riseIn { from { opacity:0; transform:translateY(18px) } to { opacity:1; transform:translateY(0) } }
    @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
    .ri { animation: riseIn 0.9s cubic-bezier(0.22,1,0.36,1) both; }
    .fi { animation: fadeIn 0.7s ease both; }
    .d1 { animation-delay:.08s } .d2 { animation-delay:.22s } .d3 { animation-delay:.38s }

    /* ── Gold rule ── */
    .gr  { height:1px; background: linear-gradient(90deg, ${GOLD} 0%, rgba(184,151,90,.06) 100%); }

    /* ── Section label ── */
    .sl { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
    .sl-line { width:20px; height:1px; background:${GOLD}; flex-shrink:0; }
    .sl-text {
      font-family:'Jost',sans-serif; font-size:13px; font-weight:600;
      letter-spacing:0.2em; text-transform:uppercase; color:${GOLD};
    }

    /* ── Prose ── */
    .policy-p {
      font-family:'Jost',sans-serif; font-size:16px; font-weight:300;
      line-height:1.9; color:#12100e; margin-bottom:16px;
    }
    .policy-p:last-child { margin-bottom:0; }
    .policy-p strong { font-weight:600; color:#334155; }

    /* ── List ── */
    .policy-ul {
      font-family:'Jost',sans-serif; font-size:15px; font-weight:300;
      line-height:1.8; color:#12100e;
      margin:0 0 16px; padding:0; list-style:none;
    }
    .policy-ul li {
      display:flex; gap:12px;
      padding:10px 0; border-bottom:1px solid #f0ece6;
    }
    .policy-ul li:last-child { border-bottom:none; }
    .policy-ul li::before {
      content:''; width:4px; height:4px; border-radius:50%;
      background:${GOLD}; flex-shrink:0; margin-top:9px;
    }
    .policy-ul strong { font-weight:600; color:#334155; }

    /* ── Table ── */
    .policy-table-wrap {
      overflow-x:auto; -webkit-overflow-scrolling:touch;
      margin:20px 0; border:1px solid #e8e4de;
      background: linear-gradient(to right, white 30%, rgba(255,255,255,0)) left center,
                  linear-gradient(to left,  white 30%, rgba(255,255,255,0)) right center,
                  linear-gradient(to right, rgba(184,151,90,.15), rgba(184,151,90,0)) left center,
                  linear-gradient(to left,  rgba(184,151,90,.15), rgba(184,151,90,0)) right center;
      background-repeat: no-repeat;
      background-size: 40px 100%, 40px 100%, 14px 100%, 14px 100%;
      background-attachment: local, local, scroll, scroll;
    }
    .policy-table {
      width:100%; min-width:540px; border-collapse:collapse;
      font-family:'Jost',sans-serif;
    }
    .policy-table th {
      font-size:12px; font-weight:600; letter-spacing:0.2em; text-transform:uppercase;
      color:${GOLD}; padding:10px 14px; background:${GOLD_DIM};
      border:1px solid ${GOLD_MID}; text-align:left; white-space:nowrap;
    }
    .policy-table td {
      font-size:15px; font-weight:300; color:#12100e;
      padding:12px 14px; border:1px solid #ece8e2;
      line-height:1.55; vertical-align:top;
    }
    .policy-table tr:nth-child(even) td { background:#faf9f7; }
    .policy-table tr:hover td          { background:${GOLD_DIM}; }

    /* ── Callout ── */
    .policy-callout {
      display:flex; gap:14px; padding:16px 18px;
      border:1px solid ${GOLD_MID}; background:${GOLD_DIM};
      margin:20px 0; position:relative; overflow:hidden;
    }
    .policy-callout::before {
      content:''; position:absolute; top:0; left:0; width:2px; height:100%;
      background:${GOLD};
    }
    .policy-callout-icon {
      width:18px; height:18px; flex-shrink:0; border:1px solid ${GOLD_MID};
      display:flex; align-items:center; justify-content:center; margin-top:2px;
    }
    .policy-callout p {
      font-family:'Jost',sans-serif; font-size:15px; font-weight:300;
      color:#12100e; line-height:1.75; margin:0;
    }
    .policy-callout strong { font-weight:600; color:#334155; }

    /* ── Section divider ── */
    .section-divider {
      height:1px;
      background:linear-gradient(90deg, transparent, rgba(184,151,90,.22), transparent);
      margin:clamp(28px, 5vw, 48px) 0;
    }

    /* ── TOC mobile accordion ── */
    .toc-mob {
      display: none;
    }
    @media(max-width:1023px) {
      .toc-mob {
        display: block;
        position: sticky;
        top: 60px;
        z-index: 100;
        background: #fff;
        border-bottom: 1px solid #e8e4de;
        box-shadow: 0 4px 20px rgba(8,14,26,0.08);
        overflow: hidden;
      }
    }

    .toc-mob-btn {
      width:100%; display:flex; align-items:center; justify-content:space-between;
      padding:14px 18px; background:transparent; cursor:pointer; border:none;
    }

    .toc-mob-body {
      overflow:hidden;
      transition:max-height .4s cubic-bezier(0.22,1,0.36,1), opacity .3s;
    }
    .toc-mob-body.open   { max-height:800px; opacity:1; }
    .toc-mob-body.closed { max-height:0;     opacity:0; }

    /* ── Sidebar TOC ── */
    .toc-desk { display:none; }
    @media(min-width:1024px) {
      .toc-desk { display:block; border:1px solid #e8e4de; background:#fff; overflow:hidden; }
    }

    /* ── Sticky sidebar ── */
    @media(min-width:1024px) {
      .policy-sidebar {
        position: sticky;
        top: 70px;                  /* snaps flush under scrolled navbar */
        align-self: start;          /* critical: prevents grid stretching sidebar full height */
        max-height: calc(100vh - 86px);
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-width: none;
      }
      .policy-sidebar::-webkit-scrollbar { display: none; }
    }

    .toc-item {
      display:flex; align-items:center; gap:10px;
      padding:10px 16px; width:100%; text-align:left;
      font-family:'Jost',sans-serif; font-size:13px; font-weight:500;
      letter-spacing:.12em; text-transform:uppercase;
      color:#12100e; cursor:pointer;
      border:none; border-left:2px solid transparent;
      background:transparent; transition:all .22s;
    }
    .toc-item:hover  { color:#12100e; background:${GOLD_DIM}; }
    .toc-item.active { color:${GOLD}; border-left-color:${GOLD}; background:${GOLD_DIM}; }
    .toc-item-num {
      font-family:'Cormorant Garamond',serif; font-size:16px;
      font-style:normal; font-weight:300; flex-shrink:0; width:18px; color:inherit;
    }

    /* ── Related page links ── */
    .related-link {
      display:flex; align-items:center; justify-content:space-between;
      padding:11px 16px; font-family:'Jost',sans-serif; font-size:13px;
      font-weight:500; letter-spacing:.15em; text-transform:uppercase;
      color:#12100e; text-decoration:none; border-bottom:1px solid #f0ece6;
      transition:all .22s;
    }
    .related-link:last-child { border-bottom:none; }
    .related-link:hover { color:${GOLD}; padding-left:20px; }
    .related-link.current { color:${GOLD}; font-weight:600; }

    /* ── Page layout ── */
    .policy-layout {
      display:grid; grid-template-columns:1fr;
      max-width:1600px; margin:0 auto;
      padding:0 18px 72px;
      gap:0;
    }
    @media(min-width:480px)  { .policy-layout { padding:0 24px 80px; } }
    @media(min-width:640px)  { .policy-layout { padding:0 32px 96px; } }
    @media(min-width:1024px) {
      .policy-layout {
        grid-template-columns:220px 1fr;
        gap:52px;
        padding:64px 32px 100px;
        align-items:start;
      }
    }

    .policy-content { min-width:0; }

    /* scroll offset — accounts for navbar + sticky TOC on mobile */
    .policy-section { scroll-margin-top:120px; }
    @media(min-width:1024px) { .policy-section { scroll-margin-top:96px; } }
  `}</style>
);

/* ── Sub-components ── */
const SL = ({ text, light }) => (
  <div className="sl">
    <div className="sl-line" style={light ? { background:'rgba(184,151,90,.55)' } : {}} />
    <span className="sl-text" style={light ? { color:'rgba(184,151,90,.7)' } : {}}>{text}</span>
  </div>
);

export const Callout = ({ children }) => (
  <div className="policy-callout">
    <div className="policy-callout-icon">
      <div style={{ width:'5px', height:'5px', background:GOLD, borderRadius:'50%' }} />
    </div>
    <p>{children}</p>
  </div>
);

export const Section = ({ id, label, title, children }) => (
  <section id={id} className="policy-section">
    <SL text={label} />
    <h2 className="f-cormorant" style={{
      fontSize:'clamp(28px,3vw,36px)', fontStyle: 'normal',
      fontWeight:300, color:NAVY, lineHeight:1.1, marginBottom:'20px',
    }}>
      {title}
    </h2>
    {children}
  </section>
);

export const PolicyTable = ({ children }) => (
  <div className="policy-table-wrap">
    <table className="policy-table">{children}</table>
  </div>
);

/* ════════════════════════════════════════
   POLICY PAGE LAYOUT
════════════════════════════════════════ */
export const PolicyPage = ({
  badge, title, subtitle, updated,
  heroImage, sections, relatedPages, currentPage,
  children,
}) => {
  const [active,  setActive]  = useState(sections[0]?.id);
  const [tocOpen, setTocOpen] = useState(false);

  /* Scroll-spy */
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin:'-10% 0px -80% 0px', threshold:0 }
    );
    sections.forEach(s => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [sections]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior:'smooth', block:'start' });
    setTocOpen(false);
  };

  return (
    <div className="f-jost" style={{ background:CR, minHeight:'100vh' }}>
      <GlobalStyles />

      {/* ── HERO ── */}
      <div className="dark-sec" style={{
        position:'relative', background:NAVY, overflow:'hidden',
        paddingTop:'clamp(5rem,12vw,7.5rem)', paddingBottom:'clamp(2.5rem,6vw,4.5rem)',
      }}>
        <img src={heroImage} alt="" style={{
          position:'absolute', inset:0, width:'100%', height:'100%',
          objectFit:'cover', opacity:.14,
        }} />
        <div style={{ position:'absolute', inset:0,
          background:'linear-gradient(115deg,rgba(8,14,26,.98) 40%,rgba(8,14,26,.62))' }} />
        <div style={{ backgroundImage:GRAIN, position:'absolute', inset:0, opacity:.5, pointerEvents:'none' }} />
        <div style={{ position:'absolute', top:'-20%', right:'28%', width:'1px', height:'160%',
          background:`linear-gradient(to bottom,transparent,${GOLD} 30%,${GOLD} 70%,transparent)`,
          opacity:.07, transform:'rotate(-7deg)', transformOrigin:'top', pointerEvents:'none' }} />

        <div style={{ position:'relative', zIndex:10, maxWidth:'1600px', margin:'0 auto', padding:'0 clamp(18px,5vw,32px)' }}>
          <Link to="/" style={{
            display:'inline-flex', alignItems:'center', gap:'8px',
            marginBottom:'clamp(16px,4vw,28px)',
            fontFamily:"'Jost',sans-serif", fontSize:'13px', fontWeight:600,
            letterSpacing:'0.16em', textTransform:'uppercase',
            color:'rgba(255,255,255,.3)', textDecoration:'none', transition:'color .25s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = GOLD}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,.3)'}
          >
            <ArrowLeft size={12} strokeWidth={1.5} /> Back to Home
          </Link>

          <div className="ri"><SL text={badge} light /></div>

          <h1 className="f-cormorant ri d1" style={{
            fontSize:'clamp(38px,6vw,76px)', fontWeight:300, fontStyle: 'normal',
            color:'#fff', lineHeight:.92, margin:'10px 0 14px',
          }}>
            {title}
          </h1>

          <p className="f-jost ri d2" style={{
            fontSize:'clamp(15px,1.5vw,18px)', fontWeight:300,
            color:'rgba(255,255,255,.42)', maxWidth:'560px',
            lineHeight:1.8, marginBottom:'clamp(20px,4vw,32px)',
          }}>
            {subtitle}
          </p>

          <div className="ri d3" style={{ display:'flex', alignItems:'center', gap:'14px', flexWrap:'wrap' }}>
            <div style={{ width:'20px', height:'1px', background:GOLD }} />
            <span className="f-jost" style={{
              fontSize:'13px', fontWeight:500, letterSpacing:'0.16em',
              textTransform:'uppercase', color:'rgba(255,255,255,.26)',
            }}>
              Last updated {updated}
            </span>
          </div>
        </div>
      </div>

      {/* ── MOBILE TOC — lives outside the grid so position:sticky works ── */}
      <div className="toc-mob">
        <div style={{ height:'2px', background:`linear-gradient(90deg,${GOLD},rgba(184,151,90,.06))` }} />
        <button className="toc-mob-btn" onClick={() => setTocOpen(o => !o)}>
          <span className="f-jost" style={{
            fontSize:'13px', fontWeight:600, letterSpacing:'0.2em', textTransform:'uppercase', color:GOLD,
          }}>
            Contents
          </span>
          <ChevronDown size={14} strokeWidth={1.5} style={{
            color:GOLD, transition:'transform .3s',
            transform: tocOpen ? 'rotate(180deg)' : 'none',
          }} />
        </button>
        <div className={`toc-mob-body ${tocOpen ? 'open' : 'closed'}`}>
          <div style={{ borderTop:'1px solid #f0ece6' }}>
            {sections.map((s, i) => (
              <button key={s.id} onClick={() => scrollTo(s.id)}
                className={`toc-item${active === s.id ? ' active' : ''}`}>
                <span className="toc-item-num">{String(i+1).padStart(2,'0')}</span>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="policy-layout">

        {/* Desktop sidebar only */}
        <aside className="policy-sidebar">

          {/* Desktop sticky TOC */}
          <div className="toc-desk">
            <div style={{ height:'2px', background:`linear-gradient(90deg,${GOLD},rgba(184,151,90,.06))` }} />
            <div style={{ padding:'16px 16px 8px' }}>
              <span className="f-jost" style={{
                fontSize:'12px', fontWeight:600, letterSpacing:'0.2em',
                textTransform:'uppercase', color:GOLD,
              }}>
                Contents
              </span>
            </div>
            <div className="gr" style={{ margin:'0 0 8px' }} />
            {sections.map((s, i) => (
              <button key={s.id} onClick={() => scrollTo(s.id)}
                className={`toc-item${active === s.id ? ' active' : ''}`}>
                <span className="toc-item-num">{String(i+1).padStart(2,'0')}</span>
                {s.label}
              </button>
            ))}

            {relatedPages?.length > 0 && (
              <>
                <div style={{ padding:'14px 16px 8px', borderTop:'1px solid #f0ece6', marginTop:'8px' }}>
                  <span className="f-jost" style={{
                    fontSize:'12px', fontWeight:600, letterSpacing:'0.2em',
                    textTransform:'uppercase', color: '#12100e',
                  }}>
                    Related
                  </span>
                </div>
                {relatedPages.map(p => (
                  <Link key={p.path} to={p.path}
                    className={`related-link${currentPage === p.path ? ' current' : ''}`}>
                    {p.label}
                    <span style={{ color:GOLD, fontSize:'13px' }}>→</span>
                  </Link>
                ))}
              </>
            )}
          </div>
        </aside>

        {/* Main content */}
        <main className="policy-content">
          {children}
        </main>

      </div>
    </div>
  );
};